import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
    Button,
    DatePicker,
    RadioButton,
    Select,
    TextInput,
} from 'components/Form'
import { createApiRecord, updateApiRecord } from 'services/RateCardService'
import { useDispatch, useSelector } from 'react-redux'
import { getRecord } from './store/dataSlice'
import reducer from './store'
import { injectReducer } from 'store'
import { Field, Form, Formik } from 'formik'
import * as Yup from 'yup'
import { Success } from 'utils/alerts'
import dayjs from 'dayjs'
import {
    getTransportProviderData,
    getTransportProviderServiceData,
} from 'store/data/dataSlice'
import WeightSlabs from './components/WeightSlabs'
injectReducer('rateCard', reducer)

const validationSchema = Yup.object().shape({
    company: Yup.number().required('Please select company'),
    client: Yup.number().nullable(),
    transport_provider: Yup.number().required(
        'Please select transport provider',
    ),
    service: Yup.number().nullable(),
    start_date: Yup.date().required('From date is required'),
    end_date: Yup.date().required('To date is required'),
    from_location: Yup.string().nullable(),
    from_zipcode: Yup.number().nullable(),
    from_longitude: Yup.number().nullable(),
    from_latitude: Yup.number().nullable(),
    to_location: Yup.string().nullable(),
    to_zipcode: Yup.number().nullable(),
    to_longitude: Yup.number().nullable(),
    to_latitude: Yup.number().nullable(),
    lane: Yup.string().nullable(),
    weight: Yup.number().nullable(),
    weight_uom: Yup.string().nullable(),
    cube: Yup.number().nullable(),
    cube_uom: Yup.string().nullable(),
    minimum: Yup.number().nullable(),
    basic: Yup.number().nullable(),
    rate: Yup.number().nullable(),
    formula: Yup.string().nullable(),
    weight_slabs: Yup.string().nullable(),
})

const Create = () => {
    const { id } = useParams()
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const [showSlabs, setShowSlabs] = useState(1)
    const [showCube, setShowCube] = useState(1)

    const {
        companyData,
        clientCompanyData,
        transportProviderData,
        transportProviderServiceData,
    } = useSelector((state) => state.listData)
    const { view, record, loading } = useSelector(
        (state) => state.rateCard.data,
    )

    useEffect(() => {
        if (id) {
            fetchData(id)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id])

    const fetchData = (id) => dispatch(getRecord(id))

    const handleDiscard = () => navigate('/master-data/rate-card')

    const handleSubmit = (values, setSubmitting) => {
        values.start_date = dayjs(values.start_date).format('YYYY-MM-DD')
        values.end_date = dayjs(values.end_date).format('YYYY-MM-DD')
        setSubmitting(true)
        if (id) {
            updateApiRecord(id, values)
                .then(() => {
                    Success('Updated Successfully!')
                    handleDiscard()
                    setSubmitting(false)
                })
                .catch((error) => {
                    setSubmitting(false)
                    return error
                })
        } else {
            createApiRecord(values)
                .then(() => {
                    Success('Created Successfully!')
                    handleDiscard()
                    setSubmitting(false)
                })
                .catch((error) => {
                    setSubmitting(false)
                    return error
                })
        }
    }

    if (loading) {
        return null
    }

    return (
        <>
            <div className="flex flex-col gap-1 mb-3 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="font-semibold text-black text-title-md2 dark:text-white">
                    Rate Card
                </h2>
            </div>
            <div className="flex flex-col items-center">
                <div className="w-full bg-white border rounded-lg border-stroke shadow-default dark:border-strokedark dark:bg-boxdark">
                    <div className="px-5 py-3 border-b border-stroke dark:border-strokedark">
                        <h3 className="font-medium text-black dark:text-white">
                            {id ? 'Update Rate Card' : 'Add New Rate Card'}
                        </h3>
                    </div>
                    <div className="flex flex-col gap-3 p-6">
                        <Formik
                            initialValues={id ? record : view}
                            validationSchema={validationSchema}
                            onSubmit={(values, { setSubmitting }) => {
                                handleSubmit(values, setSubmitting)
                            }}
                        >
                            {({ values, touched, errors, isSubmitting }) => (
                                <Form>
                                    <div className="grid grid-cols-1 gap-x-4 gap-y-4 md:grid-cols-6">
                                        <Field name="company">
                                            {({ field, form }) => (
                                                <Select
                                                    options={companyData}
                                                    label="Company"
                                                    size="sm"
                                                    placeholder="Select"
                                                    name="company"
                                                    className="md:col-span-2"
                                                    error={
                                                        errors.company &&
                                                        touched.company
                                                    }
                                                    message={errors.company}
                                                    value={values?.company}
                                                    onChange={(e) => {
                                                        form.setFieldValue(
                                                            field.name,
                                                            e.target.value,
                                                        )
                                                        form.setFieldValue(
                                                            'client',
                                                            '',
                                                        )
                                                        form.setFieldValue(
                                                            'transport_provider',
                                                            '',
                                                        )
                                                        form.setFieldValue(
                                                            'service',
                                                            '',
                                                        )
                                                        dispatch(
                                                            getTransportProviderData(
                                                                {
                                                                    company:
                                                                        e.target
                                                                            .value,
                                                                },
                                                            ),
                                                        )
                                                        dispatch(
                                                            getTransportProviderServiceData(
                                                                {
                                                                    company:
                                                                        e.target
                                                                            .value,
                                                                },
                                                            ),
                                                        )
                                                    }}
                                                />
                                            )}
                                        </Field>
                                        <Field name="client">
                                            {({ field, form }) => (
                                                <Select
                                                    options={clientCompanyData?.filter(
                                                        (item) =>
                                                            item?.client &&
                                                            item?.company?.id.toString() ===
                                                                values?.company,
                                                    )}
                                                    label="Client Company"
                                                    size="sm"
                                                    placeholder="Select"
                                                    name="client"
                                                    className="md:col-span-2"
                                                    error={
                                                        errors.client &&
                                                        touched.client
                                                    }
                                                    message={errors.client}
                                                    value={values?.client}
                                                    disabled={!values?.company}
                                                    onChange={(e) =>
                                                        form.setFieldValue(
                                                            field.name,
                                                            e.target.value,
                                                        )
                                                    }
                                                />
                                            )}
                                        </Field>
                                        <Field name="transport_provider">
                                            {({ field, form }) => (
                                                <Select
                                                    options={transportProviderData?.filter(
                                                        (item) =>
                                                            item.type ===
                                                            'Aggregator',
                                                    )}
                                                    label="Transport Provider"
                                                    size="sm"
                                                    placeholder="Select"
                                                    name="transport_provider"
                                                    className="md:col-span-2"
                                                    error={
                                                        errors.transport_provider &&
                                                        touched.transport_provider
                                                    }
                                                    message={
                                                        errors.transport_provider
                                                    }
                                                    value={
                                                        values?.transport_provider
                                                    }
                                                    disabled={!values?.company}
                                                    onChange={(e) => {
                                                        form.setFieldValue(
                                                            field.name,
                                                            e.target.value,
                                                        )
                                                    }}
                                                />
                                            )}
                                        </Field>
                                        <Field name="service">
                                            {({ field, form }) => (
                                                <Select
                                                    options={
                                                        transportProviderServiceData
                                                    }
                                                    label="Transport Provider Service"
                                                    size="sm"
                                                    placeholder="Select"
                                                    name="service"
                                                    className="md:col-span-2"
                                                    error={
                                                        errors.service &&
                                                        touched.service
                                                    }
                                                    message={errors.service}
                                                    value={values?.service}
                                                    disabled={!values?.company}
                                                    onChange={(e) =>
                                                        form.setFieldValue(
                                                            field.name,
                                                            e.target.value,
                                                        )
                                                    }
                                                />
                                            )}
                                        </Field>
                                        <Field name="start_date">
                                            {({ field, form }) => (
                                                <DatePicker
                                                    className="md:col-span-2"
                                                    label="From Date"
                                                    name="start_date"
                                                    error={
                                                        errors.start_date &&
                                                        touched.start_date
                                                    }
                                                    message={errors.start_date}
                                                    value={
                                                        values?.start_date
                                                            ? dayjs(
                                                                  values?.start_date,
                                                              ).format(
                                                                  'YYYY-MM-DD',
                                                              )
                                                            : ''
                                                    }
                                                    onChange={(e) =>
                                                        form.setFieldValue(
                                                            field.name,
                                                            e.target.value,
                                                        )
                                                    }
                                                />
                                            )}
                                        </Field>
                                        <Field name="end_date">
                                            {({ field, form }) => (
                                                <DatePicker
                                                    className="md:col-span-2"
                                                    label="To Date"
                                                    name="end_date"
                                                    error={
                                                        errors.end_date &&
                                                        touched.end_date
                                                    }
                                                    message={errors.end_date}
                                                    value={
                                                        values?.end_date
                                                            ? dayjs(
                                                                  values?.end_date,
                                                              ).format(
                                                                  'YYYY-MM-DD',
                                                              )
                                                            : ''
                                                    }
                                                    onChange={(e) =>
                                                        form.setFieldValue(
                                                            field.name,
                                                            e.target.value,
                                                        )
                                                    }
                                                />
                                            )}
                                        </Field>
                                        <Field name="from_location">
                                            {({ field, form }) => (
                                                <TextInput
                                                    type="text"
                                                    className="md:col-span-2"
                                                    label="From Location"
                                                    name="from_location"
                                                    error={
                                                        errors.from_location &&
                                                        touched.from_location
                                                    }
                                                    message={
                                                        errors.from_location
                                                    }
                                                    value={
                                                        values?.from_location
                                                    }
                                                    onChange={(e) => {
                                                        form.setFieldValue(
                                                            field.name,
                                                            e.target.value,
                                                        )
                                                        if (
                                                            values.to_location
                                                        ) {
                                                            let arr =
                                                                values.lane.split(
                                                                    '-',
                                                                )
                                                            arr[0] =
                                                                e.target.value
                                                            form.setFieldValue(
                                                                'lane',
                                                                arr.join('-'),
                                                            )
                                                        } else {
                                                            form.setFieldValue(
                                                                'lane',
                                                                `${e.target.value}-`,
                                                            )
                                                        }
                                                    }}
                                                />
                                            )}
                                        </Field>
                                        <Field name="from_zipcode">
                                            {({ field, form }) => (
                                                <TextInput
                                                    type="text"
                                                    className="md:col-span-2"
                                                    label="From Zipcode"
                                                    name="from_zipcode"
                                                    error={
                                                        errors.from_zipcode &&
                                                        touched.from_zipcode
                                                    }
                                                    message={
                                                        errors.from_zipcode
                                                    }
                                                    value={values?.from_zipcode}
                                                    onChange={(e) =>
                                                        form.setFieldValue(
                                                            field.name,
                                                            e.target.value,
                                                        )
                                                    }
                                                />
                                            )}
                                        </Field>
                                        <Field name="from_longitude">
                                            {({ field, form }) => (
                                                <TextInput
                                                    type="text"
                                                    className="md:col-span-2"
                                                    label="From Longitude"
                                                    name="from_longitude"
                                                    error={
                                                        errors.from_longitude &&
                                                        touched.from_longitude
                                                    }
                                                    message={
                                                        errors.from_longitude
                                                    }
                                                    value={
                                                        values?.from_longitude
                                                    }
                                                    onChange={(e) =>
                                                        form.setFieldValue(
                                                            field.name,
                                                            e.target.value,
                                                        )
                                                    }
                                                />
                                            )}
                                        </Field>
                                        <Field name="from_latitude">
                                            {({ field, form }) => (
                                                <TextInput
                                                    type="text"
                                                    className="md:col-span-2"
                                                    label="From Latitude"
                                                    name="from_latitude"
                                                    error={
                                                        errors.from_latitude &&
                                                        touched.from_latitude
                                                    }
                                                    message={
                                                        errors.from_latitude
                                                    }
                                                    value={
                                                        values?.from_latitude
                                                    }
                                                    onChange={(e) =>
                                                        form.setFieldValue(
                                                            field.name,
                                                            e.target.value,
                                                        )
                                                    }
                                                />
                                            )}
                                        </Field>
                                        <Field name="to_location">
                                            {({ field, form }) => (
                                                <TextInput
                                                    type="text"
                                                    className="md:col-span-2"
                                                    label="To Location"
                                                    name="to_location"
                                                    error={
                                                        errors.to_location &&
                                                        touched.to_location
                                                    }
                                                    message={errors.to_location}
                                                    value={values?.to_location}
                                                    onChange={(e) => {
                                                        form.setFieldValue(
                                                            field.name,
                                                            e.target.value,
                                                        )
                                                        if (
                                                            values.from_location
                                                        ) {
                                                            let arr =
                                                                values.lane.split(
                                                                    '-',
                                                                )
                                                            arr[1] =
                                                                e.target.value
                                                            form.setFieldValue(
                                                                'lane',
                                                                arr.join('-'),
                                                            )
                                                        } else {
                                                            form.setFieldValue(
                                                                'lane',
                                                                ` - ${e.target.value}`,
                                                            )
                                                        }
                                                    }}
                                                />
                                            )}
                                        </Field>
                                        <Field name="to_zipcode">
                                            {({ field, form }) => (
                                                <TextInput
                                                    type="text"
                                                    className="md:col-span-2"
                                                    label="To Zipcode"
                                                    name="to_zipcode"
                                                    error={
                                                        errors.to_zipcode &&
                                                        touched.to_zipcode
                                                    }
                                                    message={errors.to_zipcode}
                                                    value={values?.to_zipcode}
                                                    onChange={(e) =>
                                                        form.setFieldValue(
                                                            field.name,
                                                            e.target.value,
                                                        )
                                                    }
                                                />
                                            )}
                                        </Field>
                                        <Field name="to_longitude">
                                            {({ field, form }) => (
                                                <TextInput
                                                    type="text"
                                                    className="md:col-span-2"
                                                    label="To Longitude"
                                                    name="to_longitude"
                                                    error={
                                                        errors.to_longitude &&
                                                        touched.to_longitude
                                                    }
                                                    message={
                                                        errors.to_longitude
                                                    }
                                                    value={values?.to_longitude}
                                                    onChange={(e) =>
                                                        form.setFieldValue(
                                                            field.name,
                                                            e.target.value,
                                                        )
                                                    }
                                                />
                                            )}
                                        </Field>
                                        <Field name="to_latitude">
                                            {({ field, form }) => (
                                                <TextInput
                                                    type="text"
                                                    className="md:col-span-2"
                                                    label="To Latitude"
                                                    name="to_latitude"
                                                    error={
                                                        errors.to_latitude &&
                                                        touched.to_latitude
                                                    }
                                                    message={errors.to_latitude}
                                                    value={values?.to_latitude}
                                                    onChange={(e) =>
                                                        form.setFieldValue(
                                                            field.name,
                                                            e.target.value,
                                                        )
                                                    }
                                                />
                                            )}
                                        </Field>
                                        <Field name="lane">
                                            {({ field, form }) => (
                                                <TextInput
                                                    type="text"
                                                    className="md:col-span-2"
                                                    label="Lane"
                                                    name="lane"
                                                    error={
                                                        errors.lane &&
                                                        touched.lane
                                                    }
                                                    message={errors.lane}
                                                    value={values?.lane}
                                                    disabled
                                                />
                                            )}
                                        </Field>
                                        <div className="flex col-span-6">
                                            <p>
                                                Do you want to use the weight
                                                slabs for calculation?
                                            </p>
                                            <RadioButton
                                                group
                                                className="flex gap-5 ml-2"
                                                name="showSlabs"
                                                value={showSlabs}
                                                onChange={(e) => {
                                                    setShowSlabs(e)
                                                }}
                                                datas={[
                                                    { label: 'Yes', value: 0 },
                                                    { label: 'No', value: 1 },
                                                ]}
                                            />
                                        </div>
                                        {showSlabs ? (
                                            <>
                                                <Field name="weight">
                                                    {({ field, form }) => (
                                                        <TextInput
                                                            type="text"
                                                            className="md:col-span-2"
                                                            label="Weight"
                                                            name="weight"
                                                            error={
                                                                errors.weight &&
                                                                touched.weight
                                                            }
                                                            message={
                                                                errors.weight
                                                            }
                                                            value={
                                                                values?.weight
                                                            }
                                                            onChange={(e) =>
                                                                form.setFieldValue(
                                                                    field.name,
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                        />
                                                    )}
                                                </Field>
                                                <Field name="weight_uom">
                                                    {({ field, form }) => (
                                                        <TextInput
                                                            type="text"
                                                            className="md:col-span-2"
                                                            label="Weight Uom"
                                                            name="weight_uom"
                                                            error={
                                                                errors.weight_uom &&
                                                                touched.weight_uom
                                                            }
                                                            message={
                                                                errors.weight_uom
                                                            }
                                                            value={
                                                                values?.weight_uom
                                                            }
                                                            onChange={(e) =>
                                                                form.setFieldValue(
                                                                    field.name,
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                        />
                                                    )}
                                                </Field>
                                            </>
                                        ) : (
                                            <Field name="weight_uom">
                                                {({ field, form }) => (
                                                    <WeightSlabs
                                                        value={
                                                            values.weight_slabs
                                                        }
                                                        onChange={(value) => {
                                                            form.setFieldValue(
                                                                field.name,
                                                                value,
                                                            )
                                                        }}
                                                    />
                                                )}
                                            </Field>
                                        )}
                                        <div className="flex col-span-6">
                                            <p>
                                                Do you want to use cubic weight
                                                for calculation?
                                            </p>
                                            <RadioButton
                                                group
                                                className="flex gap-5 ml-2"
                                                name="showCube"
                                                value={showCube}
                                                onChange={(e) => {
                                                    setShowCube(e)
                                                }}
                                                datas={[
                                                    { label: 'Yes', value: 0 },
                                                    { label: 'No', value: 1 },
                                                ]}
                                            />
                                        </div>
                                        {showCube ? null : (
                                            <>
                                                <Field name="cube">
                                                    {({ field, form }) => (
                                                        <TextInput
                                                            type="text"
                                                            className="md:col-span-2"
                                                            label="Cube"
                                                            name="cube"
                                                            error={
                                                                errors.cube &&
                                                                touched.cube
                                                            }
                                                            message={
                                                                errors.cube
                                                            }
                                                            value={values?.cube}
                                                            onChange={(e) =>
                                                                form.setFieldValue(
                                                                    field.name,
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                        />
                                                    )}
                                                </Field>
                                                <Field name="cube_uom">
                                                    {({ field, form }) => (
                                                        <TextInput
                                                            type="text"
                                                            className="md:col-span-2"
                                                            label="Cube Uom"
                                                            name="cube_uom"
                                                            error={
                                                                errors.cube_uom &&
                                                                touched.cube_uom
                                                            }
                                                            message={
                                                                errors.cube_uom
                                                            }
                                                            value={
                                                                values?.cube_uom
                                                            }
                                                            onChange={(e) =>
                                                                form.setFieldValue(
                                                                    field.name,
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                        />
                                                    )}
                                                </Field>
                                            </>
                                        )}
                                        <div className="grid grid-cols-1 gap-x-4 gap-y-4 md:grid-cols-6 col-span-6">
                                            <Field name="minimum">
                                                {({ field, form }) => (
                                                    <TextInput
                                                        type="text"
                                                        className="md:col-span-2"
                                                        label="Minimum"
                                                        name="minimum"
                                                        error={
                                                            errors.minimum &&
                                                            touched.minimum
                                                        }
                                                        message={errors.minimum}
                                                        value={values?.minimum}
                                                        onChange={(e) =>
                                                            form.setFieldValue(
                                                                field.name,
                                                                e.target.value,
                                                            )
                                                        }
                                                    />
                                                )}
                                            </Field>
                                            <Field name="basic">
                                                {({ field, form }) => (
                                                    <TextInput
                                                        type="text"
                                                        className="md:col-span-2"
                                                        label="Basic"
                                                        name="basic"
                                                        error={
                                                            errors.basic &&
                                                            touched.basic
                                                        }
                                                        message={errors.basic}
                                                        value={values?.basic}
                                                        onChange={(e) =>
                                                            form.setFieldValue(
                                                                field.name,
                                                                e.target.value,
                                                            )
                                                        }
                                                    />
                                                )}
                                            </Field>
                                            <Field name="rate">
                                                {({ field, form }) => (
                                                    <TextInput
                                                        type="text"
                                                        className="md:col-span-2"
                                                        label="Rate"
                                                        name="rate"
                                                        error={
                                                            errors.rate &&
                                                            touched.rate
                                                        }
                                                        message={errors.rate}
                                                        value={values?.rate}
                                                        onChange={(e) =>
                                                            form.setFieldValue(
                                                                field.name,
                                                                e.target.value,
                                                            )
                                                        }
                                                    />
                                                )}
                                            </Field>
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-end gap-4 mt-3">
                                        <Button
                                            className="h-10 w-18 text-primary border border-primary dark:text-secondary dark:border-secondary"
                                            onClick={handleDiscard}
                                        >
                                            Discard
                                        </Button>
                                        <Button
                                            type="submit"
                                            className="flex items-center justify-center px-6 py-2 text-white transition border rounded-lg cursor-pointer hover:bg-opacity-90 border-primary bg-primary dark:border-secondary dark:bg-secondary"
                                            disabled={isSubmitting}
                                        >
                                            Save
                                        </Button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </div>
                </div>
            </div>
        </>
    )
}

export default Create
